import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { portalApi } from "@/lib/portalApi";
import { equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";
import PaymentMethodSelector from "@/components/portal/PaymentMethodSelector";

const ccyFmt = (v, ccy = "USD") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
  catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
};

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(new Date(s)); }
  catch { return s.slice(0, 10); }
};

const STATUS = {
  awaiting_payment: ["Awaiting Payment", "bg-amber-100 text-amber-700"],
  issued: ["Awaiting Payment", "bg-blue-100 text-blue-700"],
  sent: ["Awaiting Payment", "bg-blue-100 text-blue-700"],
  viewed: ["Awaiting Payment", "bg-blue-100 text-blue-700"],
  partially_paid: ["Partially Paid", "bg-cyan-100 text-cyan-700"],
  paid: ["Paid", "bg-emerald-100 text-emerald-700"],
  overdue: ["Overdue", "bg-rose-100 text-rose-700"],
  payment_submitted: ["Payment Verification Pending", "bg-amber-100 text-amber-700"],
  overpayment: ["Overpayment", "bg-orange-100 text-orange-700"],
  refunded: ["Refunded", "bg-purple-100 text-purple-700"],
};

// Canonical client-facing method labels (P10)
const METHOD_LABELS = {
  stripe:    "Credit / Debit Card",
  card:      "Credit / Debit Card",
  paypal:    "PayPal",
  wise:      "Wise",
  thai_bank: "Thai Direct Bank Transfer",
  thai_qr:   "Thai QR Code",
  wire:      "International Wire Transfer",
  // Staff-only aliases — shown on receipts/history only
  ach: "ACH", bank_transfer: "Bank Transfer",
  thai_bank_transfer: "Thai Bank Transfer", wire_transfer: "Wire Transfer",
  cash: "Cash", check: "Check", manual: "Manual",
};
const fmtMethod = (m) => METHOD_LABELS[m] || (m || "").replace(/_/g, " ");

export default function PortalBilling() {
  const [activeTab, setActiveTab] = useState("invoices");
  const [data, setData] = useState(null);
  const [receiptsData, setReceiptsData] = useState(null);
  const [open, setOpen] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [sp, setSp] = useSearchParams();
  const [cc, setCc] = useState({ usd_thb_rate: 33, show_thb: false });
  const navigate = useNavigate();
  const eq = (v, cur) => equivalentFor(v, cur || "USD", { rate: cc.usd_thb_rate, showThb: cc.show_thb });

  const load = useCallback(() => {
    portalApi.get("/portal/billing").then((r) => setData(r.data)).catch(() => setData({ invoices: [], paid_invoices: [], summary: {} }));
  }, []);

  const loadReceipts = useCallback(() => {
    portalApi.get("/portal/receipts").then((r) => setReceiptsData(r.data)).catch(() => setReceiptsData({ receipts: [], total: 0 }));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReceipts(); }, [loadReceipts]);
  useEffect(() => { portalApi.get("/portal/currency-context").then((r) => r?.data && setCc(r.data)).catch(() => {}); }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.info("Still processing — refresh in a moment to see your payment."); return; }
    try {
      const { data: st } = await portalApi.get(`/portal/billing/checkout/status/${sessionId}`);
      if (st.payment_status === "paid") {
        setVerifying(false);
        let info = null;
        try { info = JSON.parse(sessionStorage.getItem("tf_portal_pending_payment") || "null"); } catch {}
        sessionStorage.removeItem("tf_portal_pending_payment");
        const returnPath = sessionStorage.getItem("tf_portal_payment_return");
        sessionStorage.removeItem("tf_portal_payment_return");
        if (returnPath) {
          navigate(returnPath, { replace: true });
        } else {
          navigate("/portal", { replace: true, state: { paymentConfirmed: true, paymentInfo: info } });
        }
        return;
      }
      if (st.status === "expired") { setVerifying(false); toast.error("This payment session expired. Please try again."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch { setVerifying(false); toast.error("Could not verify payment status."); }
  }, [navigate]);

  useEffect(() => {
    const sessionId = sp.get("session_id");
    if (sessionId) { setVerifying(true); pollStatus(sessionId); setSp({}, { replace: true }); }
  }, [sp, setSp, pollStatus]);

  // Auto-open a specific invoice when ?invoice={id} is in the URL
  useEffect(() => {
    const targetId = sp.get("invoice");
    if (targetId && data?.invoices) {
      const match = data.invoices.find((i) => i.id === targetId);
      if (match) {
        setOpen(targetId);
        setActiveTab("invoices");
        setTimeout(() => {
          const el = document.getElementById(`invoice-row-${targetId}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
      }
    }
  }, [sp, data]);

  // URL tab parameter routing
  useEffect(() => {
    const tab = sp.get("tab");
    if (tab === "receipts") { setActiveTab("receipts"); setSp({}, { replace: true }); }
    else if (tab === "paid") { setActiveTab("paid"); setSp({}, { replace: true }); }
  }, [sp, setSp]);

  const downloadReceipt = async (receiptId, receiptNumber) => {
    try {
      const res = await portalApi.get(`/portal/billing/receipts/${receiptId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${receiptNumber || "receipt"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the receipt."); }
  };

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await portalApi.get(`/portal/billing/${invoiceId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${invoiceNumber || invoiceId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the invoice PDF."); }
  };

  const downloadPaidInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await portalApi.get(`/portal/billing/${invoiceId}/paid-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${invoiceNumber || invoiceId}_PAID.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not download the Paid Invoice PDF."); }
  };

  const s = data?.summary || {};
  const paidInvoices = data?.paid_invoices || [];
  const invoices = data?.invoices || [];

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-billing">
      {/* Header */}
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Billing &amp; Payments</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your invoices, balances, and payment receipt history.</p>
      </div>

      {verifying && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center gap-2.5 text-sm text-blue-700" data-testid="portal-billing-verifying">
          <Icons.Loader2 className="h-4 w-4 animate-spin" />Verifying your payment…
        </div>
      )}

      {/* Summary card */}
      {data && (
        <div className="bg-[#171B2E] text-white rounded-2xl p-5 shadow-sm mb-5 flex items-center justify-between" data-testid="portal-billing-summary">
          <div>
            <div className="text-xs text-slate-300 uppercase tracking-wide">Outstanding Balance</div>
            <div className="text-3xl font-bold mt-1">{ccyFmt(s.outstanding_balance, s.currency)}</div>
            {eq(s.outstanding_balance, s.currency) && <div className="text-xs text-slate-400 mt-0.5">≈ {eq(s.outstanding_balance, s.currency)}</div>}
          </div>
          <div className="text-right">
            <div className="h-12 w-12 rounded-full bg-[#F5A623] flex items-center justify-center ml-auto"><Icons.Receipt className="h-6 w-6 text-[#171B2E]" /></div>
            <div className="text-xs text-slate-400 mt-1">{s.open_count || 0} open</div>
          </div>
        </div>
      )}

      {/* ── Three Tabs ──────────────────────────────────────────────────────── */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 overflow-x-auto" data-testid="portal-billing-tabs">
        <button
          onClick={() => setActiveTab("invoices")}
          data-testid="portal-billing-tab-invoices"
          className={`flex-1 min-w-max flex items-center justify-center gap-1.5 text-sm font-medium py-2 px-3 rounded-lg transition-all ${
            activeTab === "invoices" ? "bg-white text-[#171B2E] shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Icons.FileText className="h-4 w-4 shrink-0" />
          <span>Invoices</span>
          {data && invoices.length > 0 && (
            <span className="bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{invoices.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("paid")}
          data-testid="portal-billing-tab-paid"
          className={`flex-1 min-w-max flex items-center justify-center gap-1.5 text-sm font-medium py-2 px-3 rounded-lg transition-all ${
            activeTab === "paid" ? "bg-white text-[#171B2E] shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Icons.BadgeCheck className="h-4 w-4 shrink-0" />
          <span>Paid Invoices</span>
          {data && paidInvoices.length > 0 && (
            <span className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{paidInvoices.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("receipts")}
          data-testid="portal-billing-tab-receipts"
          className={`flex-1 min-w-max flex items-center justify-center gap-1.5 text-sm font-medium py-2 px-3 rounded-lg transition-all ${
            activeTab === "receipts" ? "bg-white text-[#171B2E] shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Icons.Download className="h-4 w-4 shrink-0" />
          <span>Payment Receipts</span>
          {receiptsData && receiptsData.total > 0 && (
            <span className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{receiptsData.total}</span>
          )}
        </button>
      </div>

      {/* ── Invoices Tab ─────────────────────────────────────────────────────── */}
      {activeTab === "invoices" && (
        <>
          {!data ? (
            <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200" data-testid="portal-no-invoices">
              <Icons.FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />No invoices yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="portal-invoice-list">
              {invoices.map((inv) => {
                const [label, tone] = STATUS[inv.status] || [inv.status, "bg-slate-100 text-slate-600"];
                const expanded = open === inv.id;
                return (
                  <div key={inv.id} id={`invoice-row-${inv.id}`}
                    className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${expanded ? "border-blue-300 ring-2 ring-blue-100" : "border-slate-200"}`}
                    data-testid={`portal-invoice-${inv.id}`}>
                    <button onClick={() => setOpen(expanded ? null : inv.id)}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50"
                      data-testid={`portal-invoice-toggle-${inv.id}`}>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-slate-800">{inv.invoice_number}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${tone}`}>{label}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {inv.issue_date ? `Issued ${inv.issue_date.slice(0, 10)}` : "—"}{inv.due_date ? ` · Due ${inv.due_date.slice(0, 10)}` : ""}
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div>
                          <div className="font-bold text-slate-900">{ccyFmt(inv.total, inv.currency)}</div>
                          {eq(inv.total, inv.currency) && <div className="text-[11px] text-slate-400">≈ {eq(inv.total, inv.currency)}</div>}
                          {inv.balance > 0 && <div className="text-xs text-rose-600">Balance {ccyFmt(inv.balance, inv.currency)}</div>}
                        </div>
                        <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                    {expanded && (
                      <div className="px-5 pb-5 border-t border-slate-100 pt-4 text-sm" data-testid={`portal-invoice-detail-${inv.id}`}>
                        <div className="mb-4" data-testid={`portal-invoice-bill-to-${inv.id}`}>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Bill To</div>
                          {inv.bill_to ? (
                            <>
                              <div className="text-sm font-medium text-slate-800">{inv.bill_to.name}</div>
                              {inv.bill_to.address && (() => {
                                const a = inv.bill_to.address;
                                const lines = [
                                  a.line1, a.line2,
                                  [a.city, [a.region, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", "),
                                  a.country
                                ].filter(Boolean);
                                return lines.map((l, i) => (
                                  <div key={i} className="text-xs text-slate-500">{l}</div>
                                ));
                              })()}
                            </>
                          ) : (
                            <div className="text-sm font-medium text-slate-800">{inv.client_name || "Client"}</div>
                          )}
                        </div>

                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Services</div>
                        <div className="rounded-lg border border-slate-100 overflow-hidden mb-3" data-testid={`portal-invoice-lineitems-${inv.id}`}>
                          {inv.line_items.map((li, idx) => (
                            <div key={idx} className="flex items-start justify-between px-3 py-2.5 border-b border-slate-50 last:border-0 gap-3 hover:bg-slate-50/50">
                              <div className="flex-1 min-w-0">
                                <div className="text-slate-800 font-medium text-sm truncate">{li.name || li.description || "Service"}</div>
                                {li.description && li.name && li.description !== li.name && (
                                  <div className="text-xs text-slate-400 truncate">{li.description}</div>
                                )}
                              </div>
                              <div className="text-right shrink-0 text-xs text-slate-500 self-center">
                                {(li.quantity && li.quantity > 1) && <span className="mr-2 text-slate-400">×{li.quantity}</span>}
                                <span className="font-semibold text-slate-800">{ccyFmt(li.amount, inv.currency)}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 space-y-1.5 mb-3" data-testid={`portal-invoice-totals-${inv.id}`}>
                          <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span>{ccyFmt(inv.total, inv.currency)}</span></div>
                          {(inv.amount_paid > 0 || (inv.payments && inv.payments.length > 0)) && (
                            <div className="flex justify-between text-sm text-emerald-600"><span>Payments received</span><span>{ccyFmt(inv.amount_paid || 0, inv.currency)}</span></div>
                          )}
                          <div className="border-t border-slate-200 pt-1.5 flex justify-between text-sm font-bold text-slate-900">
                            <span>Balance Due</span>
                            <span className={inv.balance <= 0 ? "text-emerald-600" : "text-slate-900"}>{ccyFmt(Math.max(0, inv.balance), inv.currency)}</span>
                          </div>
                          {eq(inv.total, inv.currency) && <div className="text-[10px] text-slate-400 pt-0.5"><FxDisclaimer /></div>}
                        </div>

                        {inv.payments && inv.payments.length > 0 && (
                          <div className="space-y-1.5 mb-3" data-testid={`portal-invoice-payments-${inv.id}`}>
                            {inv.payments.map((p, pi) => (
                              <div key={pi} className="flex items-center justify-between text-xs bg-emerald-50/60 rounded-lg px-3 py-2">
                                <span className="text-slate-600">
                                  {(() => {
                                    const isXC = p.settlement_currency &&
                                      p.settlement_currency.toUpperCase() !== (inv.currency || "USD").toUpperCase() &&
                                      p.settlement_amount != null;
                                    const displayAmt = isXC && p.invoice_currency_amount != null
                                      ? p.invoice_currency_amount
                                      : p.amount;
                                    return (
                                      <>
                                        <span data-testid={`portal-payment-amt-${pi}`}>{ccyFmt(displayAmt, inv.currency)}</span>
                                        {isXC && (
                                          <span className="text-[10px] text-slate-400 ml-1" data-testid={`portal-payment-settlement-${pi}`}>
                                            ({p.settlement_currency?.toUpperCase()} {Number(p.settlement_amount).toFixed(2)} settled)
                                          </span>
                                        )}
                                        {" · "}{(p.method || "").replace(/_/g, " ")}{p.received_date ? ` · ${p.received_date.slice(0, 10)}` : ""}
                                      </>
                                    );
                                  })()}
                                </span>
                                {p.receipt_id && (
                                  <button onClick={() => downloadReceipt(p.receipt_id, p.receipt_number)}
                                    data-testid={`portal-download-receipt-${p.receipt_id}`}
                                    className="inline-flex items-center gap-1 text-[#171B2E] hover:text-[#F5A623] font-medium">
                                    <Icons.Download className="h-3.5 w-3.5" />Receipt
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {inv.payment_plan && (
                          <div className="mt-3 bg-slate-50 rounded-lg p-3">
                            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Payment Plan</div>
                            {inv.payment_plan.installments.map((it) => (
                              <div key={it.id} className="flex justify-between text-xs text-slate-600 py-0.5">
                                <span>{it.label}{it.due_date ? ` · ${it.due_date.slice(0, 10)}` : ""}</span>
                                <span>{ccyFmt(it.amount, inv.currency)}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {inv.notes_client && inv.notes_client.length <= 400 && (
                          <p className="text-xs text-slate-500 mt-3 italic" data-testid={`portal-invoice-note-${inv.id}`}>{inv.notes_client}</p>
                        )}

                        {/* Download / Print / Paid Invoice actions */}
                        <div className="flex flex-wrap gap-2 mt-4" data-testid={`portal-invoice-actions-${inv.id}`}>
                          <button
                             onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number)}
                             className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                             data-testid={`portal-invoice-download-${inv.id}`}>
                            <Icons.Download className="h-3.5 w-3.5" />Download PDF
                          </button>
                          <button
                             onClick={async () => {
                               const { data } = await portalApi.get(`/portal/billing/${inv.id}/pdf`, { responseType: "blob" });
                               const url = URL.createObjectURL(data);
                               const w = window.open(url, "_blank"); w?.focus();
                             }}
                             className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                             data-testid={`portal-invoice-print-${inv.id}`}>
                            <Icons.Printer className="h-3.5 w-3.5" />Print Invoice
                          </button>
                          {inv.status === "paid" && inv.balance <= 0 && (
                            <button
                               onClick={() => downloadPaidInvoicePdf(inv.id, inv.invoice_number)}
                               className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-medium transition-colors"
                               data-testid={`portal-invoice-paid-pdf-${inv.id}`}>
                              <Icons.BadgeCheck className="h-3.5 w-3.5" />Paid Invoice
                            </button>
                          )}
                        </div>

                        {(inv.is_payable || inv.status === "payment_submitted") && (
                          <div className="mt-4" data-testid={`portal-payment-selector-${inv.id}`}>
                            <PaymentMethodSelector invoiceId={inv.id} onReportSubmitted={load} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Paid Invoices Tab ─────────────────────────────────────────────── */}
      {activeTab === "paid" && (
        <div data-testid="portal-paid-invoices">
          <div className="mb-3">
            <p className="text-sm text-slate-500">
              Fully settled invoices with zero outstanding balance. Download your Paid Invoice PDF for each.
            </p>
          </div>
          {!data ? (
            <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>
          ) : paidInvoices.length === 0 ? (
            <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200" data-testid="portal-no-paid-invoices">
              <Icons.BadgeCheck className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <div className="font-medium text-slate-500 mb-1">No paid invoices yet</div>
              <div className="text-xs text-slate-400">Invoices will appear here once fully settled.</div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="portal-paid-invoice-list">
              {paidInvoices.map((inv) => (
                <div
                  key={inv.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4"
                  data-testid={`portal-paid-invoice-${inv.id}`}
                >
                  {/* Top row: invoice number + Paid in Full badge */}
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-slate-800" data-testid={`portal-paid-inv-number-${inv.id}`}>
                          {inv.invoice_number}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid={`portal-paid-inv-status-${inv.id}`}>
                          Paid in Full
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {inv.issue_date ? `Issued ${inv.issue_date.slice(0, 10)}` : "—"}
                        {inv.payment_completion_date
                          ? ` · Completed ${fmtDate(inv.payment_completion_date)}`
                          : ""}
                      </div>
                    </div>
                    {/* Amount block */}
                    <div className="text-right shrink-0">
                      <div className="font-bold text-slate-900" data-testid={`portal-paid-inv-total-${inv.id}`}>
                        {ccyFmt(inv.total, inv.currency)}
                      </div>
                      <div className="text-xs text-emerald-600 font-medium" data-testid={`portal-paid-inv-balance-${inv.id}`}>
                        {ccyFmt(0, inv.currency)} outstanding
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100" data-testid={`portal-paid-invoice-actions-${inv.id}`}>
                    <button
                      onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                      data-testid={`portal-paid-inv-original-pdf-${inv.id}`}
                    >
                      <Icons.Download className="h-3.5 w-3.5" />Original Invoice
                    </button>
                    <button
                      onClick={() => downloadPaidInvoicePdf(inv.id, inv.invoice_number)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-medium transition-colors"
                      data-testid={`portal-paid-inv-paid-pdf-${inv.id}`}
                    >
                      <Icons.BadgeCheck className="h-3.5 w-3.5" />Paid Invoice PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {data && paidInvoices.length > 0 && (
            <p className="text-center text-xs text-slate-400 mt-4" data-testid="portal-paid-invoices-count">
              {paidInvoices.length} paid invoice{paidInvoices.length !== 1 ? "s" : ""} on file
            </p>
          )}
        </div>
      )}

      {/* ── Payment Receipts Tab ──────────────────────────────────────────────── */}
      {activeTab === "receipts" && (
        <div data-testid="portal-receipts-archive">
          <div className="mb-3">
            <p className="text-sm text-slate-500">
              Your complete payment receipt history. All receipts are permanently retained here regardless of invoice or engagement status.
            </p>
          </div>

          {!receiptsData ? (
            <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>
          ) : receiptsData.receipts.length === 0 ? (
            <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200" data-testid="portal-no-receipts">
              <Icons.Receipt className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <div className="font-medium text-slate-500 mb-1">No payment receipts yet</div>
              <div className="text-xs text-slate-400">Receipts will appear here once payments have been processed.</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="portal-receipt-list">
              {receiptsData.receipts.map((r) => {
                // Part A receipt display fix:
                // - Primary: authoritative invoice-currency credit (r.amount = invoice_currency_amount for XC, or raw receipt amount)
                // - Secondary: settlement info only when cross-currency verified settlement data is present
                // - Do NOT show ≈ approximate conversion when verified settlement evidence exists
                const hasSettlement = r.settlement_currency && r.settlement_amount != null;
                return (
                  <div key={r.id}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 flex items-center justify-between gap-4"
                    data-testid={`portal-receipt-row-${r.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                        <Icons.Receipt className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-slate-800" data-testid={`portal-receipt-number-${r.id}`}>
                            {r.receipt_number || "—"}
                          </span>
                          {r.invoice_number && (
                            <span className="text-[11px] text-slate-400">· {r.invoice_number}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {fmtDate(r.paid_at || r.created_at)}
                          {r.payment_method && <span> · {fmtMethod(r.payment_method)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        {/* Primary: invoice-currency credit (authoritative) */}
                        <div className="font-bold text-slate-900" data-testid={`portal-receipt-amount-${r.id}`}>
                          {ccyFmt(r.amount, r.currency)}
                        </div>
                        {/* Secondary: processor settlement — only shown when cross-currency verified */}
                        {hasSettlement ? (
                          <div className="text-[11px] text-slate-400" data-testid={`portal-receipt-settlement-${r.id}`}>
                            {r.settlement_currency.toUpperCase()} {Number(r.settlement_amount).toFixed(2)} settled
                          </div>
                        ) : (
                          /* For same-currency or legacy records without settlement data, show approx conversion */
                          eq(r.amount, r.currency) && (
                            <div className="text-[11px] text-slate-400">≈ {eq(r.amount, r.currency)}</div>
                          )
                        )}
                      </div>
                      <button
                        onClick={() => downloadReceipt(r.id, r.receipt_number)}
                        data-testid={`portal-archive-download-receipt-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-sm bg-[#171B2E] text-white hover:bg-[#F5A623] hover:text-[#171B2E] font-medium px-3 py-1.5 rounded-lg transition-colors"
                        title={`Download ${r.receipt_number}`}
                      >
                        <Icons.Download className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Download</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {receiptsData && receiptsData.total > 0 && (
            <p className="text-center text-xs text-slate-400 mt-4" data-testid="portal-receipt-archive-count">
              {receiptsData.total} receipt{receiptsData.total !== 1 ? "s" : ""} on file
            </p>
          )}
        </div>
      )}

      <p className="text-center text-xs text-slate-400 mt-6">Secured by Tax Flow · Expat Tax Firm</p>
    </div>
  );
}
